<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useWorkspaceStore } from '../stores/workspace';
import type { PrototypeBlock } from '../types/entities';
import PrototypeBlockNode from './PrototypeBlockNode.vue';
import EntityPicker from './EntityPicker.vue';
import { createSerializedSaveQueue } from '../lib/serializedSaveQueue';

type PrototypeUpdatePatch = Partial<Pick<PrototypeBlock, 'content' | 'dateLabel' | 'linkedEntityId' | 'parentId' | 'sortOrder'>>;
type PrototypeUpdateRequest = { workspaceId: string; blockId: string; patch: PrototypeUpdatePatch };

const props = defineProps<{ initialScopeKind?: PrototypeBlock['scopeKind']; initialScopeEntityId?: string | null }>();
const emit = defineEmits<{ openEntity: [id: string] }>();
const store = useWorkspaceStore();
const { prototypeBlocks, entities, templates, categories } = storeToRefs(store);
const scopeKind = ref<PrototypeBlock['scopeKind']>(props.initialScopeKind ?? 'story');
const scopeEntityId = ref(props.initialScopeEntityId ?? '');
const busy = ref(false);
const error = ref('');
const prototypeUpdateJournal = new Map<string, PrototypeUpdateRequest>();
function prototypeRequestKey(request: PrototypeUpdateRequest) { return `${request.workspaceId}:${request.blockId}`; }
function persistPrototypeJournal(workspaceId: string) {
  if (typeof window === 'undefined') return;
  const storageKey = `chronis.prototype-blocks.v1:${workspaceId}`;
  try {
    const entries = [...prototypeUpdateJournal.values()].filter((request) => request.workspaceId === workspaceId);
    if (entries.length) window.sessionStorage.setItem(storageKey, JSON.stringify(entries));
    else window.sessionStorage.removeItem(storageKey);
  } catch { /* Keep the in-memory retry path when browser storage is unavailable. */ }
}
const prototypeUpdateQueue = createSerializedSaveQueue(async (request: PrototypeUpdateRequest) => {
  await store.updatePrototypeBlock(request.blockId, request.patch);
  const key = prototypeRequestKey(request);
  if (prototypeUpdateJournal.get(key) === request) {
    prototypeUpdateJournal.delete(key);
    persistPrototypeJournal(request.workspaceId);
  }
}, prototypeRequestKey);

const chapterOptions = computed(() => entities.value.filter((item) => item.status === 'active' && item.templateCategory === 'kapitel'));
const sceneOptions = computed(() => entities.value.filter((item) => item.status === 'active' && item.templateCategory === 'szene'));
const scopeOptions = computed(() => scopeKind.value === 'chapter' ? chapterOptions.value : sceneOptions.value);
const selectedScopeEntityId = computed(() => scopeKind.value === 'story' || !scopeEntityId.value ? null : scopeEntityId.value);
const currentBlocks = computed(() => prototypeBlocks.value.filter((item) => item.scopeKind === scopeKind.value && item.scopeEntityId === selectedScopeEntityId.value));
const rootBlocks = computed(() => currentBlocks.value.filter((item) => !item.parentId).sort((a, b) => a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt)));
const scopeTitle = computed(() => scopeKind.value === 'story'
  ? 'Gesamte Geschichte'
  : scopeOptions.value.find((item) => item.id === scopeEntityId.value)?.title ?? `${scopeKind.value === 'chapter' ? 'Kapitel' : 'Szene'} ohne Referenz`);

watch(scopeKind, () => {
  if (scopeKind.value === 'story') scopeEntityId.value = '';
  else if (!scopeOptions.value.some((item) => item.id === scopeEntityId.value)) scopeEntityId.value = '';
});
watch(() => props.initialScopeEntityId, (id) => { if (id) scopeEntityId.value = id; });

async function run(action: () => Promise<unknown>) {
  error.value = '';
  try { await action(); }
  catch (reason) { error.value = reason instanceof Error ? reason.message : 'Der Prototyp konnte nicht gespeichert werden.'; }
}
function validPrototypePatch(value: unknown): value is PrototypeUpdatePatch {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const entries = Object.entries(value);
  return entries.length > 0 && entries.every(([key, item]) =>
    ['content', 'parentId', 'sortOrder', 'dateLabel', 'linkedEntityId'].includes(key)
      && (key === 'sortOrder' ? typeof item === 'number' && Number.isFinite(item)
        : key === 'parentId' || key === 'dateLabel' || key === 'linkedEntityId' ? item === null || typeof item === 'string'
          : typeof item === 'string')
  );
}
async function loadPrototypeWorkspace(workspaceId: string) {
  try {
    await store.loadPrototypeBlocks();
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : 'Prototyp-Blöcke konnten nicht geladen werden.';
    return;
  }
  if (store.workspace?.id !== workspaceId || typeof window === 'undefined') return;
  const storageKey = `chronis.prototype-blocks.v1:${workspaceId}`;
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(storageKey) ?? '[]') as unknown;
    const validBlocks = new Set(store.prototypeBlocks.map((block) => block.id));
    if (Array.isArray(saved)) for (const entry of saved.slice(0, 500)) {
      if (!entry || typeof entry !== 'object') continue;
      const request = entry as Partial<PrototypeUpdateRequest>;
      if (request.workspaceId !== workspaceId || typeof request.blockId !== 'string' || !validBlocks.has(request.blockId) || !validPrototypePatch(request.patch)) continue;
      const normalized = request as PrototypeUpdateRequest;
      prototypeUpdateJournal.set(prototypeRequestKey(normalized), normalized);
    }
    persistPrototypeJournal(workspaceId);
  } catch {
    try { window.sessionStorage.removeItem(storageKey); } catch { /* Browser storage may be disabled. */ }
  }
  for (const request of [...prototypeUpdateJournal.values()].filter((item) => item.workspaceId === workspaceId)) {
    void prototypeUpdateQueue.enqueue(request).catch((reason) => {
      error.value = reason instanceof Error ? reason.message : 'Ein gespeicherter Prototyp-Entwurf konnte nicht wiederhergestellt werden.';
    });
  }
}
function queuePrototypeUpdate(blockId: string, patch: PrototypeUpdatePatch) {
  const workspaceId = store.workspace?.id;
  if (!workspaceId) return Promise.reject(new Error('Keine aktive Welt für den Prototyp-Speicherpfad.'));
  const request = { workspaceId, blockId, patch: { ...(prototypeUpdateJournal.get(`${workspaceId}:${blockId}`)?.patch ?? {}), ...patch } };
  prototypeUpdateJournal.set(prototypeRequestKey(request), request);
  persistPrototypeJournal(workspaceId);
  return prototypeUpdateQueue.enqueue(request);
}
async function retryPrototypeUpdates() {
  try {
    await prototypeUpdateQueue.flush();
    error.value = '';
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : 'Prototyp-Änderungen konnten nicht gespeichert werden.';
  }
}
async function focusBlock(id: string) {
  await nextTick();
  document.querySelector<HTMLTextAreaElement>(`[data-prototype-id="${id}"] textarea`)?.focus();
}
async function createBlock(parentId: string | null = null, sortOrder?: number) {
  if (busy.value) return;
  busy.value = true;
  await run(async () => {
    const block = await store.createPrototypeBlock({
      parentId,
      scopeKind: scopeKind.value,
      scopeEntityId: selectedScopeEntityId.value,
      ...(sortOrder !== undefined ? { sortOrder } : {})
    });
    if (block) await focusBlock(block.id);
  });
  busy.value = false;
}
async function createChild(block: PrototypeBlock) {
  const children = currentBlocks.value.filter((item) => item.parentId === block.id);
  await createBlock(block.id, Math.max(0, ...children.map((item) => item.sortOrder)) + 10);
}
async function createAfter(block: PrototypeBlock) {
  const siblings = currentBlocks.value.filter((item) => item.parentId === block.parentId).sort((a, b) => a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt));
  const index = siblings.findIndex((item) => item.id === block.id);
  const next = siblings[index + 1];
  const order = next && next.sortOrder - block.sortOrder > 1 ? Math.floor((block.sortOrder + next.sortOrder) / 2) : block.sortOrder + 1;
  await createBlock(block.parentId, order);
}
async function updateBlock(id: string, patch: PrototypeUpdatePatch, done?: (failure?: string) => void) {
  try {
    error.value = '';
    await queuePrototypeUpdate(id, patch);
    done?.();
  } catch (reason) {
    const message = reason instanceof Error ? reason.message : 'Der Prototyp konnte nicht gespeichert werden.';
    error.value = message;
    done?.(message);
  }
}
async function moveBlock(block: PrototypeBlock, direction: -1 | 1) {
  const siblings = currentBlocks.value.filter((item) => item.parentId === block.parentId).sort((a, b) => a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt));
  const index = siblings.findIndex((item) => item.id === block.id);
  const other = siblings[index + direction];
  if (!other) return;
  await run(async () => {
    const ownOrder = block.sortOrder;
    await queuePrototypeUpdate(block.id, { sortOrder: other.sortOrder });
    await queuePrototypeUpdate(other.id, { sortOrder: ownOrder });
  });
}
async function indentBlock(block: PrototypeBlock) {
  const siblings = currentBlocks.value.filter((item) => item.parentId === block.parentId).sort((a, b) => a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt));
  const previous = siblings[siblings.findIndex((item) => item.id === block.id) - 1];
  if (!previous) return;
  const children = currentBlocks.value.filter((item) => item.parentId === previous.id);
  await run(() => queuePrototypeUpdate(block.id, { parentId: previous.id, sortOrder: Math.max(0, ...children.map((item) => item.sortOrder)) + 10 }));
}
async function outdentBlock(block: PrototypeBlock) {
  const parent = currentBlocks.value.find((item) => item.id === block.parentId);
  if (!parent) return;
  await run(() => queuePrototypeUpdate(block.id, { parentId: parent.parentId, sortOrder: parent.sortOrder + 1 }));
}
function descendantCount(blockId: string) {
  const found = new Set([blockId]);
  let changed = true;
  while (changed) { changed = false; for (const item of currentBlocks.value) if (item.parentId && found.has(item.parentId) && !found.has(item.id)) { found.add(item.id); changed = true; } }
  return found.size - 1;
}
async function removeBlock(block: PrototypeBlock) {
  const children = descendantCount(block.id);
  if (!confirm(children ? `Block und ${children} Unterblock${children === 1 ? '' : 'e'} wirklich löschen?` : 'Diesen Prototyp-Block wirklich löschen?')) return;
  await run(() => store.deletePrototypeBlock(block.id));
}
async function convertBlock(block: PrototypeBlock, templateId: string, title: string, content: string, dateLabel: string) {
  await run(async () => {
    if (content !== block.content || (dateLabel.trim() || null) !== block.dateLabel) {
      await queuePrototypeUpdate(block.id, { content, dateLabel: dateLabel.trim() || null });
    }
    await store.convertPrototypeBlock(block.id, templateId, title);
  });
}

watch(() => store.workspace?.id, (workspaceId) => { if (workspaceId) void loadPrototypeWorkspace(workspaceId); }, { immediate: true });
onBeforeUnmount(() => { void prototypeUpdateQueue.flush().catch(() => {}); });
</script>

<template>
  <section class="rapid-prototype-view">
    <header class="rapid-prototype-header">
      <div><span>Rapid Prototyping</span><h1>{{ scopeTitle }}</h1><p>Grobe Abläufe festhalten, ordnen und erst später in echte Chronis-Entities überführen.</p></div>
      <button type="button" :disabled="busy" @click="createBlock()">＋ Leerer Block</button>
    </header>
    <nav class="prototype-scope-tabs" aria-label="Prototyp-Bereich">
      <button type="button" :class="{ active: scopeKind === 'story' }" @click="scopeKind = 'story'">Gesamtgeschichte</button>
      <button type="button" :class="{ active: scopeKind === 'chapter' }" @click="scopeKind = 'chapter'">Kapitel</button>
      <button type="button" :class="{ active: scopeKind === 'scene' }" @click="scopeKind = 'scene'">Szene</button>
      <EntityPicker v-if="scopeKind !== 'story'" :model-value="scopeEntityId || null" :entities="entities" :templates="templates" :categories="categories" :allowed-categories="[scopeKind === 'chapter' ? 'kapitel' : 'szene']" clear-label="Keine Referenz" :aria-label="scopeKind === 'chapter' ? 'Kapitel wählen' : 'Szene wählen'" @update:model-value="scopeEntityId = $event ?? ''" />
    </nav>
    <div class="prototype-hints"><span>Strg + Enter: Block darunter</span><span>Alt + Enter: Unterblock</span><span>Datum und Entity sind optional</span></div>
    <p v-if="error" class="prototype-error">{{ error }} <button v-if="prototypeUpdateQueue.hasFailed()" type="button" @click="retryPrototypeUpdates">Erneut versuchen</button></p>
    <div class="prototype-canvas">
      <PrototypeBlockNode v-for="block in rootBlocks" :key="block.id" :data-prototype-id="block.id" :block="block" :blocks="currentBlocks" :entities="entities" :templates="templates" :categories="categories" @update="updateBlock" @create-child="createChild" @create-after="createAfter" @remove="removeBlock" @move="moveBlock" @indent="indentBlock" @outdent="outdentBlock" @convert="convertBlock" @open-entity="emit('openEntity', $event)" />
      <button v-if="!rootBlocks.length" type="button" class="prototype-empty" @click="createBlock()"><strong>＋ Ersten Block anlegen</strong><span>Noch keine Struktur nötig. Schreib einfach den ersten Gedanken auf.</span></button>
      <button v-else type="button" class="prototype-canvas-add" @click="createBlock()">＋ Weiteren Block anhängen</button>
    </div>
  </section>
</template>

<style scoped>
.rapid-prototype-view{min-height:100%;padding:clamp(20px,3vw,42px);background:radial-gradient(circle at 70% 0,color-mix(in srgb, var(--ui-surface-3) 20%, transparent),transparent 42%)}.rapid-prototype-header{display:flex;align-items:flex-start;justify-content:space-between;gap:24px;max-width:1060px;margin:0 auto 18px}.rapid-prototype-header span{color:var(--ui-action-text);font-size:10px;font-weight:900;letter-spacing:.12em;text-transform:uppercase}.rapid-prototype-header h1{margin:5px 0 5px;color:var(--ui-text-soft);font-size:clamp(25px,3vw,38px)}.rapid-prototype-header p{max-width:670px;margin:0;color:var(--ui-text-soft);line-height:1.5}.rapid-prototype-header>button{flex:0 0 auto;border:0;border-radius:9px;color:var(--ui-action-text);background:var(--ui-action);padding:10px 14px;font-weight:850;box-shadow:0 0 20px color-mix(in srgb, var(--ui-action) 26%, transparent)}.rapid-prototype-header>button:disabled{opacity:.4}.prototype-scope-tabs{display:flex;align-items:center;gap:5px;max-width:1060px;margin:0 auto;padding:5px;border:1px solid var(--ui-border);border-radius:10px;background:var(--ui-surface-1)}.prototype-scope-tabs button{border:0;border-radius:7px;color:var(--ui-text-soft);background:transparent;padding:8px 11px;font-size:11px;font-weight:800}.prototype-scope-tabs button.active{color:var(--ui-text-soft);background:var(--ui-surface-3);box-shadow:inset 0 0 0 1px var(--ui-accent)}.prototype-scope-tabs select{min-width:230px;margin-left:auto;border:1px solid var(--ui-border);border-radius:7px;color:var(--ui-text-soft);background:var(--ui-surface-2);padding:8px}.prototype-hints{display:flex;gap:18px;max-width:1060px;margin:9px auto 18px;color:var(--ui-text-muted);font-size:9px}.prototype-error{max-width:1060px;margin:0 auto 12px;border:1px solid var(--ui-border);border-radius:7px;color:var(--ui-danger-text);background:var(--ui-surface-2);padding:8px 10px}.prototype-canvas{max-width:1060px;min-height:480px;margin:0 auto;padding:18px 12px 80px;border:1px solid var(--ui-border);border-radius:14px;background:linear-gradient(180deg,color-mix(in srgb, var(--ui-surface-1) 85%, transparent),color-mix(in srgb, var(--ui-surface-1) 72%, transparent))}.prototype-empty{display:flex;min-height:260px;width:100%;align-items:center;justify-content:center;flex-direction:column;gap:7px;border:1px dashed var(--ui-border);border-radius:11px;color:var(--ui-action-text);background:var(--ui-surface-1)}.prototype-empty span{color:var(--ui-text-muted);font-size:11px}.prototype-canvas-add{margin:9px 0 0 38px;border:1px dashed var(--ui-border);border-radius:7px;color:var(--ui-accent);background:transparent;padding:8px 12px;font-size:11px}.prototype-no-scope{display:flex;align-items:center;justify-content:center;flex-direction:column;gap:6px;min-height:360px;color:var(--ui-text-soft)}.prototype-no-scope span{color:var(--ui-text-muted);font-size:11px}@media(max-width:760px){.rapid-prototype-view{padding:16px 8px}.rapid-prototype-header{flex-direction:column}.prototype-scope-tabs{flex-wrap:wrap}.prototype-scope-tabs select{width:100%;margin-left:0}.prototype-hints{display:none}.prototype-canvas{padding-inline:5px}}
.prototype-scope-tabs .entity-picker{min-width:230px;margin-left:auto;flex:0 1 300px}@media(max-width:760px){.prototype-scope-tabs .entity-picker{width:100%;margin-left:0}}
</style>
